import { createSlice } from '@reduxjs/toolkit';

/**
 * Visitor Management — visitor TYPES.
 *
 * A visitor type owns an ordered list of check-in steps (`StepsJson`) and its own behaviour
 * settings, and it is what makes a Contractor's check-in differ from a Visitor's. Reads and writes
 * `/v5/:orgID/visits/types`.
 *
 * Modelled on Redux/dayPasses: org-keyed data, per-operation loading flags, a one-shot
 * `mutationComplete` for the editor to close on, and an `auth/logout` reset.
 *
 * 🛑 SCOPE: types only, deliberately. Visits themselves (Today / On-site / History) are a separate
 * surface with different read patterns — paginated, date-filtered, and refetched on a socket event
 * — and folding them into this slice would give the type editor a cache that invalidates every
 * time somebody checks in downstairs.
 */

const emptyOrg = () => ({
	types: [], // every visitor type in the org: global (siteID null) + per-site
	/**
	 * Site config, keyed by the level it belongs to: 'org' for the org default, otherwise the
	 * siteID. Each entry is { orgRow, siteRow, resolved } exactly as the API returns it.
	 *
	 * 🛑 THE RAW ROWS ARE KEPT, NOT JUST `resolved`. The editor must be able to tell an INHERITED
	 * value from an OVERRIDDEN one — otherwise saving a site whose colour merely showed through
	 * from the org writes that colour onto the site as an override the admin never chose, and the
	 * two levels silently stop tracking each other.
	 */
	siteConfig: {},
	// Every lobby tablet in the org, from GET /visitor-kiosks. Never holds a pairing code or a
	// token — the list endpoint returns `tokenMasked` only, and the code lives in `pairingIssue`.
	kiosks: [],
	// Visit RECORDS for the current query window (GET /visits). Keyed only by org; the view owns the
	// site/date/status query, so re-querying replaces the window.
	records: [],
	/**
	 * Server-computed visit stats (GET /visits/stats): onSiteNow, onSiteAtSameTimeYesterday,
	 * expectedToday {total, registered, walkIn}, overdue, todayTotal, upcoming, deniedToday,
	 * arrivalsByHour[24], departuresByHour[24], last7Days[7].
	 *
	 * ⚠️ `null` means "no server stats" — either not loaded yet or the endpoint is not deployed on
	 * this API — and the summary falls back to client-derived counts from `records`. The fallback is
	 * why a stats load failure is deliberately QUIET (no toast): the page is fully usable without it.
	 */
	stats: null
});

const initialState = {
	data: {}, // data[orgID] = emptyOrg()
	loading: false,
	loadingComplete: false,
	typesLoadError: null,
	/**
	 * In-flight flags, ONE PER WRITE FAMILY (audit L6). `mutationLoading` is visitor-type writes;
	 * site-config and kiosk writes have their own. They used to share this one boolean, so the FIRST
	 * write to finish cleared it for all of them: a quick tablet revoke landing during a type save
	 * read as "the type save ended" — the editor stopped waiting on its own save and never closed.
	 * Selectors combine them where a screen genuinely means "any of these" (selectors.js).
	 */
	mutationLoading: false,
	siteConfigMutationLoading: false,
	kioskMutationLoading: false,
	/**
	 * One-shot. The editor watches this to close itself after a successful save, then the reducer
	 * clears it on the next mutation start. A boolean rather than the saved row because the editor
	 * must not re-seed its form from a response — that is how an in-flight edit gets clobbered.
	 */
	mutationComplete: false,
	createdType: null,
	// Separate from `loading`: the branding screen reads config while the types list may also be
	// loading, and one flag for both makes each spinner lie about the other.
	configLoading: false,
	// Same argument again for the tablet list, which loads beside both of the above.
	kiosksLoading: false,
	visitsLoading: false,
	statsLoading: false,
	/**
	 * The visitID of the lifecycle action in flight, or null. Per-visit rather than a page flag so
	 * one row's "Check out" spinner cannot mask every other row's actions; the saga is takeEvery, so
	 * two different rows acting at once is legitimate (last writer wins this indicator — cosmetic).
	 */
	actionVisitID: null,
	creatingVisit: false,
	// One-shot: the New Visit modal's OWN close signal — see createVisitSuccess.
	createVisitComplete: false,
	/**
	 * The pairing code from the last create or re-pair: `{ kioskDeviceID, pairingCode,
	 * pairingExpiresAt }`.
	 *
	 * 🛑 THE SERVER STORES ONLY ITS HASH, SO THIS IS THE ONLY COPY THAT WILL EVER EXIST.
	 * It is cleared by an explicit dismiss and by NOTHING else — not by the refetch that follows
	 * the write, not by a re-render, not by switching tabs. An auto-clear would take the code off
	 * the screen while the installer is still typing it into the tablet, and the only way to get
	 * another is to re-pair, which for an already-paired tablet KILLS ITS LIVE CREDENTIAL.
	 */
	pairingIssue: null,
	/**
	 * One-shot: a kiosk write finished successfully.
	 *
	 * 🛑 SEPARATE FROM `pairingIssue`, because a create can succeed WITHOUT a code — the saga
	 * models exactly that — and the add form was closing on the code alone. In that case the form
	 * stayed open with no error, the operator read it as 'it did not save', pressed Create again,
	 * and minted a second kiosk. One signal cannot mean both 'the write landed' and 'here is a
	 * credential to show'.
	 */
	kioskMutationComplete: false,
	/**
	 * One-shot: a site-config save landed. SEPARATE FROM `mutationComplete` for the same reason
	 * `kioskMutationComplete` is separate from it — `mutationComplete` is TypeEditor's close signal,
	 * cleared only while that drawer is mounted. Sharing one flag meant a site-config save completing
	 * while TypeEditor happened to be open (or a type save completing while Settings had a pending
	 * save) closed/cleared the WRONG screen — each screen must read only ITS OWN write's outcome.
	 */
	siteConfigMutationComplete: false,
	error: null
};

const ensureOrg = (state, orgID) => {
	if (!state.data[orgID]) state.data[orgID] = emptyOrg();
	return state.data[orgID];
};

const visitsSlice = createSlice({
	name: 'visits',
	initialState,
	reducers: {
		loadTypes: (state) => {
			state.loading = true;
			state.loadingComplete = false;
			state.error = null;
			state.typesLoadError = null;
		},
		loadTypesSuccess: (state, { payload }) => {
			ensureOrg(state, payload.orgID).types = payload.types || [];
			state.loading = false;
			state.loadingComplete = true;
		},
		loadTypesError: (state, { payload }) => {
			state.loading = false;
			state.loadingComplete = true;
			state.error = payload?.message || String(payload);
			// Its OWN field: `error` is shared by every visits operation, and the type list must not
			// read a failed save as a failed load, nor a failed load as "this org has no types".
			state.typesLoadError = payload?.message || String(payload);
		},

		// One lifecycle for create / update / delete — the editor cares that a write finished, not
		// which verb it was.
		mutateType: (state) => {
			state.mutationLoading = true;
			state.mutationComplete = false;
			state.error = null;
		},
		mutateTypeSuccess: (state, { payload } = {}) => {
			state.mutationLoading = false;
			state.mutationComplete = true;
			// The type a CREATE just wrote (null for update/delete) — read by the editor once, then
			// cleared with mutationComplete.
			state.createdType = payload || null;
		},
		mutateTypeError: (state, { payload }) => {
			state.mutationLoading = false;
			// 🛑 mutationComplete stays FALSE on an error. The editor closes on it, so setting it
			// here would dismiss the form on a failed save and lose the operator's work — with a
			// toast as the only clue that nothing was written.
			state.error = payload?.message || String(payload);
		},
		clearMutationComplete: (state) => {
			state.mutationComplete = false;
			state.createdType = null;
		},
		clearSiteConfigMutationComplete: (state) => {
			state.siteConfigMutationComplete = false;
		},

		loadSiteConfig: (state) => {
			state.configLoading = true;
			state.error = null;
		},
		loadSiteConfigSuccess: (state, { payload }) => {
			// `levelKey` is 'org' or the siteID — see emptyOrg().
			ensureOrg(state, payload.orgID).siteConfig[payload.levelKey] = payload.config || null;
			state.configLoading = false;
		},
		loadSiteConfigError: (state, { payload }) => {
			state.configLoading = false;
			state.error = payload?.message || String(payload);
		},

		saveSiteConfig: (state) => {
			state.siteConfigMutationLoading = true;
			state.siteConfigMutationComplete = false;
			state.error = null;
		},
		saveSiteConfigSuccess: (state) => {
			state.siteConfigMutationLoading = false;
			state.siteConfigMutationComplete = true;
		},
		saveSiteConfigError: (state, { payload }) => {
			// siteConfigMutationComplete stays FALSE, for the reason mutateTypeError gives.
			state.siteConfigMutationLoading = false;
			state.error = payload?.message || String(payload);
		},

		/* ── Lobby tablets (kiosks) ──────────────────────────────────────────────────────────── */

		loadKiosks: (state) => {
			state.kiosksLoading = true;
			state.error = null;
		},
		loadKiosksSuccess: (state, { payload }) => {
			ensureOrg(state, payload.orgID).kiosks = payload.kiosks || [];
			state.kiosksLoading = false;
		},
		loadKiosksError: (state, { payload }) => {
			state.kiosksLoading = false;
			state.error = payload?.message || String(payload);
		},
		loadVisits: (state) => {
			state.visitsLoading = true;
			state.error = null;
		},
		loadVisitsSuccess: (state, { payload }) => {
			ensureOrg(state, payload.orgID).records = payload.records || [];
			// The server's total exceeded the page: the grid is showing a PREFIX of the window.
			ensureOrg(state, payload.orgID).recordsTruncated = Boolean(payload.truncated);
			state.visitsLoading = false;
		},
		loadVisitsError: (state, { payload }) => {
			state.visitsLoading = false;
			state.error = payload?.message || String(payload);
		},

		loadVisitStats: (state) => {
			state.statsLoading = true;
		},
		loadVisitStatsSuccess: (state, { payload }) => {
			ensureOrg(state, payload.orgID).stats = payload.stats || null;
			state.statsLoading = false;
		},
		/**
		 * 🛑 QUIET, and it does NOT touch `state.error`. Stats are an enhancement over the
		 * client-derived fallback — while /visits/stats is not yet deployed on an API, every page
		 * load would otherwise toast an error for a page that works. The saga is equally silent.
		 */
		loadVisitStatsError: (state) => {
			state.statsLoading = false;
		},

		/**
		 * A visit lifecycle transition (check-in / check-out / cancel / deny / no-show / invite).
		 * The saga POSTs the transition route and refetches the window; nothing is patched into
		 * `records` locally — the server owns the status machine and the event trail.
		 */
		visitAction: (state, { payload }) => {
			state.actionVisitID = payload?.visitID ?? null;
			state.error = null;
		},
		visitActionSuccess: (state) => {
			state.actionVisitID = null;
		},
		visitActionError: (state, { payload }) => {
			state.actionVisitID = null;
			state.error = payload?.message || String(payload);
		},

		/**
		 * POST /visits — schedule a brand-new visit (New Visit modal). Server behaviour: the visit is
		 * created `scheduled`; no invite is sent automatically (`create()` never emails — see
		 * `handoff.md`), so the modal chains a `visitAction('invite')` after this succeeds.
		 *
		 * 🛑 `createVisitComplete` is its OWN dedicated one-shot, not the shared `mutationComplete` —
		 * round-2 review on the Wave F PR caught exactly this class of bug (a shared flag read by an
		 * unrelated screen as its own success). This flag has exactly one consumer: the New Visit
		 * modal, closing itself.
		 */
		createVisit: (state) => {
			state.creatingVisit = true;
			state.createVisitComplete = false;
			state.error = null;
		},
		createVisitSuccess: (state) => {
			state.creatingVisit = false;
			state.createVisitComplete = true;
		},
		createVisitError: (state, { payload }) => {
			state.creatingVisit = false;
			// createVisitComplete stays FALSE, for the reason mutateTypeError gives — the modal must
			// stay open with the operator's entries intact on a failed create.
			state.error = payload?.message || String(payload);
		},
		clearCreateVisitComplete: (state) => {
			state.createVisitComplete = false;
		},

		// One lifecycle for create / re-pair / revoke, as mutateType does for types.
		mutateKiosk: (state) => {
			state.kioskMutationLoading = true;
			state.kioskMutationComplete = false;
			state.error = null;
		},
		/**
		 * `payload.issue` is present for create and re-pair and absent for revoke.
		 *
		 * 🛑 A revoke must NOT clear a code that is on screen. The two are independent: an
		 * operator can revoke tablet B while still typing tablet A's code, and wiping it there would
		 * destroy the only copy over an unrelated action.
		 */
		mutateKioskSuccess: (state, { payload }) => {
			state.kioskMutationLoading = false;
			state.kioskMutationComplete = true;
			if (payload?.issue) state.pairingIssue = payload.issue;
		},
		mutateKioskError: (state, { payload }) => {
			state.kioskMutationLoading = false;
			state.error = payload?.message || String(payload);
		},
		// The ONLY thing that clears a pairing code. See initialState.pairingIssue.
		dismissPairingCode: (state) => {
			state.pairingIssue = null;
		},
		clearKioskMutationComplete: (state) => {
			state.kioskMutationComplete = false;
		}
	},
	extraReducers: (builder) => {
		builder.addCase('auth/logout', () => initialState);
	}
});

export const visitsActions = visitsSlice.actions;
export default visitsSlice.reducer;
